/** Family screens emit commands here, never to the transport or database. */
import { announceFamilyJoin, announceFamilyDeletion } from "@/lib/family_notice";
import { FAMILY_LIVE_INVITE_LIMIT } from "../../shared/family_limits";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  FamilyHttpError,
  SessionExpiredError,
  previewFamily,
  inviteFamily,
  revokeFamilyInvite,
  renameFamily,
  updateFamilyMe,
  removeFamilyMember,
  transferFamilyOwner,
  type FamilyCredential,
  type FamilyInvite,
  type FamilyPreview,
} from "@/lib/backend";
import {
  cachedFamily,
  refreshFamily,
  cacheFamily,
  createFamilyLedger,
  joinFamilyLedger,
  clearFamilyView,
  familyExpenseCount,
  activeMembers,
  nextFamilyOwner,
  FAMILY_LIMIT,
} from "@/lib/family";
import { leaveFamily, getRemovedFamilyRows, type LeaveOutcome } from "@/lib/session";
import { familyDb } from "@/lib/db";
import { subscribeFamilySync } from "@/lib/sync";
import { useAuth } from "./use_auth";
import { useProfile } from "./use_data";
import { usePlanGate } from "./use_plan_gate";
import { haptics as haptic } from "@/lib/haptics";
import { nativeBridge } from "@/native/runtime";

export const JOIN_ERRORS: Readonly<Record<string, string>> = {
  invalid_code: "That code doesn't match an invite. Check it with the person who sent it.",
  invite_expired: "This invite has expired. Ask for a new one.",
  invite_used: "This invite has already been used.",
  invite_revoked: "This invite was cancelled.",
  too_many_previews: "Too many invite links opened today. Try again tomorrow.",
  too_many_attempts: "Too many wrong codes today. Try again tomorrow.",
  already_in_family: "You're already in a family. Leave it first to join another.",
};
const MEMBERSHIP_ERRORS: Readonly<Record<string, string>> = {
  too_many_invites: `There are already ${FAMILY_LIVE_INVITE_LIMIT} live invites. Revoke one to make another.`,
  not_a_member: "You're no longer in this family. Check Settings › Family for any unsent changes.",
  not_owner: "Only the owner can do this. Refresh the family and try again.",
  confirm_required: "Leaving deletes the family ledger. Confirm Leave and delete to continue.",
  member_not_found: "That member has already left.",
  not_found: "That member has already left.",
  update_required: "Update Tide Tally to use families.",
  use_leave: "Use Leave family to leave your own membership.",
};
export function familyError(cause: unknown, name = "The family"): string {
  if (cause instanceof FamilyHttpError)
    return cause.code === "family_full"
      ? `${name} is full (${FAMILY_LIMIT} of ${FAMILY_LIMIT}).`
      : JOIN_ERRORS[cause.code] ?? MEMBERSHIP_ERRORS[cause.code] ?? cause.message;
  if (cause instanceof Error && ["TypeError", "TimeoutError"].includes(cause.name))
    return "Could not reach the family. Check your connection and try again.";
  return cause instanceof Error ? cause.message : "Could not reach the family. Check your connection and try again.";
}
export function useFamily() {
  const { userId, state, requestReauth } = useAuth();
  const defaults = useProfile();
  const plan = usePlanGate(userId);
  const [family, setFamily] = useState<Awaited<ReturnType<typeof cachedFamily>>>(null);
  const [loaded, setLoaded] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const inviteGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<FamilyPreview | null>(null);
  const [invite, setInvite] = useState<FamilyInvite | null>(null);
  const [blocked, setBlocked] = useState<Extract<LeaveOutcome, { kind: "blocked" }> | null>(null);
  const removed = useLiveQuery(getRemovedFamilyRows, [], null);
  const profile = useLiveQuery(() => familyDb.profile.toCollection().first(), []);
  const expenseCount = useLiveQuery(familyExpenseCount, [], 0);
  const refresh = useCallback(async () => {
    setFamily(await cachedFamily());
    setLoaded(true);
    if (navigator.onLine && state === "signed-in") setFamily(await refreshFamily());
    setLoaded(true);
  }, [state]);
  useEffect(() => {
    let alive = true;
    const read = () => {
      void refresh().catch((cause: unknown) => {
        if (alive) {
          setError(familyError(cause));
          setLoaded(true);
        }
      });
    };
    read();
    const connection = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) read();
    };
    window.addEventListener("online", connection);
    window.addEventListener("offline", connection);
    const unsubscribe = subscribeFamilySync(() => {
      if (alive) {
        void cachedFamily()
          .then((value) => {
            if (alive) setFamily(value);
          })
          .catch((cause: unknown) => {
            if (alive) setError(familyError(cause));
          });
      }
    });
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener("online", connection);
      window.removeEventListener("offline", connection);
    };
  }, [refresh]);
  async function run<T>(action: () => Promise<T>, offline: string): Promise<T | undefined> {
    if (running.current) return;
    setError(null);
    if (!navigator.onLine) {
      setError(offline);
      haptic("error");
      return;
    }
    if (state !== "signed-in") {
      requestReauth();
      setError("Sign in, and the invite opens again.");
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      const result = await action();
      haptic(result === false ? "error" : "success");
      return result;
    } catch (cause) {
      if (cause instanceof SessionExpiredError) requestReauth();
      setError(familyError(cause, preview?.name));
      haptic("error");
      return;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  async function mutate(action: () => Promise<{ family: typeof family }>): Promise<boolean> {
    return (
      (await run(async () => {
        const result = await action();
        await cacheFamily(result.family);
        setFamily(result.family);
        return true;
      }, "You're offline. Changing family details needs a connection.")) ?? false
    );
  }
  const members = family ? activeMembers(family) : [];
  const me = members.find((member) => member.member_id === family?.my_member_id);
  return {
    family: removed ? null : family,
    previousFamily: family,
    members,
    me,
    owner: me?.role === "owner",
    nextOwner: family ? nextFamilyOwner(family) : undefined,
    full: members.length >= FAMILY_LIMIT,
    loaded,
    online,
    busy,
    error,
    notice,
    preview,
    invite,
    blocked,
    removed,
    profile,
    defaults,
    expenseCount,
    readOnly: plan.entitlement?.readOnly ?? false,
    signedIn: state === "signed-in",
    requestReauth,
    clearError: () => setError(null),
    clearPreview: () => {
      previewGeneration.current += 1;
      setPreview(null);
    },
    clearInvite: () => {
      inviteGeneration.current += 1;
      setInvite(null);
    },
    clearBlocked: () => setBlocked(null),
    refresh,
    create: (name: string, display: string) =>
      run(async () => {
        const value = await createFamilyLedger(name, display, {
          cycleStartDay: defaults.cycleStartDay,
          currency: defaults.currency,
          timezone: defaults.timezone,
          rolloverMode: defaults.rolloverMode,
        });
        setFamily(value);
        return true;
      }, "You're offline. Creating a family needs a connection."),
    previewInvite: (credential: FamilyCredential) =>
      run(async () => {
        const generation = previewGeneration.current;
        const value = await previewFamily(credential);
        if (generation === previewGeneration.current) setPreview(value);
        return true;
      }, "You're offline. Joining needs a connection."),
    join: (credential: FamilyCredential, display: string) =>
      run(async () => {
        const value = await joinFamilyLedger(credential, display);
        setFamily(value);
        setNotice(`You joined ${value.name}`);
        announceFamilyJoin(value.name);
        setPreview(null);
        return true;
      }, "You're offline. Joining needs a connection."),
    newInvite: () =>
      run(async () => {
        const generation = inviteGeneration.current;
        const value = await inviteFamily();
        if (generation === inviteGeneration.current) setInvite(value);
        await refresh();
        return true;
      }, "You're offline. Invites need a connection."),
    revoke: (id: string) => mutate(() => revokeFamilyInvite(id)),
    saveDetails: (name: string, display: string) =>
      run(async () => {
        const renamed = await renameFamily(name.trim());
        await cacheFamily(renamed.family);
        setFamily(renamed.family);
        const value = await updateFamilyMe(display.trim());
        await cacheFamily(value.family);
        setFamily(value.family);
        return true;
      }, "You're offline. Changing family details needs a connection."),
    remove: (id: string) => mutate(() => removeFamilyMember(id)),
    transfer: (id: string) => mutate(() => transferFamilyOwner(id)),
    leave: (confirmDelete: boolean, discard = false) =>
      run(async () => {
        const result = await leaveFamily(confirmDelete, { discard });
        if (result.kind === "blocked") {
          setBlocked(result);
          return false;
        }
        setFamily(null);
        if (confirmDelete) {
          announceFamilyDeletion(family?.name ?? "The family");
          setNotice(`${family?.name ?? "The family"} was deleted by its last member. Your own ledger isn't affected.`);
        }
        if (result.kind !== "removed") await clearFamilyView();
        return true;
      }, "You're offline. Leaving needs a connection."),
    copy: async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setNotice("Copied");
        haptic("success");
      } catch (cause) {
        setError(familyError(cause));
        haptic("error");
      }
    },
    share: async (url: string, code: string) => {
      try {
        const bridge = nativeBridge();
        if (bridge.share)
          await bridge.share({ title: `Invite to ${family?.name ?? "Family"}`, text: `${url}\n${code}` });
        else if ("share" in navigator && typeof navigator.share === "function")
          await navigator.share({ title: `Invite to ${family?.name ?? "Family"}`, text: code, url });
        else {
          await navigator.clipboard.writeText(url);
          setNotice("Copied");
        }
      } catch (cause) {
        if (cause instanceof Error && cause.name === "AbortError") return;
        setError(familyError(cause));
        haptic("error");
      }
    },
  };
}
export type FamilyState = ReturnType<typeof useFamily>;
